import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import type { OpsCapacityErrorResponse, OpsCapacityResponse } from './types';

/** Hard stop so the page never spins forever on a slow replica. */
export const REQUEST_TIMEOUT_MS = 20_000;

export interface OpsCapacityState {
  /** Last successful payload; kept on screen while a newer range loads. */
  data: OpsCapacityResponse | null;
  /** True while a request for the committed range is in flight. */
  loading: boolean;
  /** True when `data` belongs to a previous range (stale-while-revalidate). */
  stale: boolean;
  error: string | null;
  /** True when the failure was a 403 — hide the dashboard, not just the data. */
  forbidden: boolean;
  refetch: () => void;
}

interface HttpError {
  response?: { status?: number; data?: OpsCapacityErrorResponse };
}

const isHttpError = (err: unknown): err is HttpError =>
  typeof err === 'object' && err !== null && 'response' in err;

export interface CommittedRange {
  /** Either may be undefined to let the server pick its default. */
  from?: string;
  to?: string;
}

/**
 * Exactly one GET /platform-stats/ops-capacity per committed range. A newer
 * range aborts the in-flight request and a late response for an older range is
 * dropped, so the charts can never show a mix of two selections.
 */
export function useOpsCapacity(range: CommittedRange): OpsCapacityState {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<OpsCapacityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [nonce, setNonce] = useState(0);

  const controllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  const hasDataRef = useRef(false);
  const { from, to } = range;

  useEffect(() => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestIdRef.current === requestId;
    let timedOut = false;

    setLoading(true);
    setError(null);
    setStale(hasDataRef.current);

    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);
    const params = new URLSearchParams();
    if (from) params.set('startDate', from);
    if (to) params.set('endDate', to);
    const qs = params.toString();

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<OpsCapacityResponse>(
          `/platform-stats/ops-capacity${qs ? `?${qs}` : ''}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        );
        if (!isCurrent() || controller.signal.aborted) return;
        hasDataRef.current = true;
        setData(res.data);
        setStale(false);
        setForbidden(false);
        setLoading(false);
      } catch (err) {
        if (!isCurrent()) return;
        if (controller.signal.aborted) {
          if (!timedOut) return;
          setError(
            `Timed out after ${REQUEST_TIMEOUT_MS / 1000}s — try a shorter range.`,
          );
        } else if (isHttpError(err)) {
          const status = err.response?.status;
          if (status === 403) setForbidden(true);
          setError(
            err.response?.data?.error ??
              (status ? `Request failed (${status})` : 'Network error'),
          );
        } else {
          setError('Failed to load ops capacity stats');
        }
        setLoading(false);
      } finally {
        clearTimeout(timer);
      }
    })();

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [from, to, nonce, getAccessTokenSilently]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, stale, error, forbidden, refetch };
}
